import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchSlots, openSlots, slotStarted, addDays, startOfDay, fmtDayHead, fmtTime, dayKey, FAINT } from "./thModel";

const PARTS = [["Morning", 0, 12], ["Afternoon", 12, 17], ["Evening", 17, 24]];

// §9.2 — shared by Reschedule and the booking tab. value = slot iso (naive local).
export function TimePicker({ cid, npi, buffer = 0, value, onChange, initialDate, onDay, testid = "th-tp" }) {
  const today = startOfDay(new Date());
  const [day, setDay] = useState(() => (initialDate && startOfDay(initialDate) >= today ? startOfDay(initialDate) : today));
  const { data, isLoading } = useQuery({ queryKey: ["th-slots", cid, npi, dayKey(day)], enabled: !!npi, queryFn: () => fetchSlots(cid, npi, day), staleTime: 15_000 });
  const slots = openSlots(data);
  const [next, setNext] = useState(null);
  useEffect(() => {
    setNext(null);
    if (isLoading || slots.length || !npi) return undefined;
    let alive = true;
    (async () => {
      for (let i = 1; i <= 21 && alive; i += 1) {
        const d = addDays(day, i);
        const r = await fetchSlots(cid, npi, d).catch(() => null);
        if (r?.slots?.length) { if (alive) setNext({ d, s: r.slots[0] }); return; }
      }
    })();
    return () => { alive = false; };
  }, [isLoading, slots.length, cid, npi, day]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onDay?.(day); }, [day]); // eslint-disable-line react-hooks/exhaustive-deps
  const isToday = dayKey(day) === dayKey(today);
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2">
        <button type="button" className="th-ibtn" disabled={isToday} style={{ opacity: isToday ? 0.35 : 1 }} onClick={() => setDay(addDays(day, -1))} aria-label="Previous day" data-testid={`${testid}-prev`}><ChevronLeft size={16} /></button>
        <div className="text-[13px] font-semibold min-w-[150px] text-center" data-testid={`${testid}-day`}>{fmtDayHead(day)}{isToday ? " · Today" : ""}</div>
        <button type="button" className="th-ibtn" onClick={() => setDay(addDays(day, 1))} aria-label="Next day" data-testid={`${testid}-next`}><ChevronRight size={16} /></button>
      </div>
      <div className="text-[12px] mt-1" style={{ color: FAINT }} data-testid={`${testid}-sub`}>{slots.length} open · {data?.slot || 30}-min slots · {buffer}-min buffer</div>
      {isLoading ? <div className="text-[12.5px] mt-3" style={{ color: FAINT }}>Loading times…</div> : !slots.length ? (
        <div className="mt-3 text-[12.5px]" data-testid={`${testid}-none`}>
          <div>No open times on {fmtDayHead(day)}.</div>
          {next ? <button type="button" className="mt-1 font-semibold" style={{ color: "#4A4AC2" }} onClick={() => { setDay(startOfDay(next.d)); onChange(next.s.iso); }} data-testid={`${testid}-nextopen`}>Next opening · {fmtDayHead(next.d)}, {fmtTime(new Date(next.s.iso))}</button> : null}
        </div>
      ) : PARTS.map(([label, a, b]) => {
        const xs = slots.filter((s) => { const h = new Date(s.iso).getHours(); return h >= a && h < b; });
        return xs.length ? (
          <div key={label} className="mt-3">
            <div className="th-lab mb-1.5">{label}</div>
            <div className="flex flex-wrap gap-1.5">{xs.map((s) => <button key={s.iso} type="button" className="th-slot" aria-pressed={value === s.iso} onClick={() => onChange(s.iso)} data-testid={`${testid}-slot-${s.time}`}>{slotStarted(s) ? `Now · ${fmtTime(new Date(s.iso))}` : fmtTime(new Date(s.iso))}</button>)}</div>
          </div>
        ) : null;
      })}
    </div>
  );
}
